import { useState, useEffect } from "react";
import { Copy, Check, ZoomIn, X } from "lucide-react";
import { C } from "../../theme/tokens";
import { SectionHeader } from "../../components/common/primitives";
import { CopyTicketCard } from "./CopyTicketCard";
import { labelStyle } from "../../theme/tokens";
import settingsUk from "../../assets/copytrading/settings-uk.svg";
import settingsRu from "../../assets/copytrading/settings-ru.svg";
import settingsEn from "../../assets/copytrading/settings-en.svg";

// The Pocket Option copy-trading account to follow. Fixed value —
// same for every user, so it's just a constant rather than something
// pulled from the user/backend.
const COPYTRADE_ID = "141971618";

// Settings screenshot is locale-specific (Pocket Option's own UI is
// localized), so pick the asset that matches the app's current
// language. Falls back to English for any language without a
// dedicated screenshot yet.
const SCREENSHOTS = { uk: settingsUk, ru: settingsRu, en: settingsEn };

export function CopyTradingScreen({ t, lang, wide }) {
  const [copied, setCopied] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const screenshotSrc = SCREENSHOTS[lang] || SCREENSHOTS.en;

  // Close the zoomed screenshot on Escape (desktop/wide layout has a
  // keyboard, and it's a cheap add for anyone using one on mobile too).
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomed(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COPYTRADE_ID);
    } catch (e) {
      // Clipboard API unavailable (older WebView, no HTTPS, etc.) —
      // fail silently, the ID is still shown and selectable by hand.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const steps = [
    { t: t.copytrade_step1_t, d: t.copytrade_step1_d },
    { t: t.copytrade_step2_t, d: t.copytrade_step2_d },
    { t: t.copytrade_step3_t, d: t.copytrade_step3_d },
    { t: t.copytrade_step4_t, d: t.copytrade_step4_d },
  ];

  return (
    <div>
      <SectionHeader title={t.copytrade_title} sub={t.copytrade_sub} wide={wide} />

      <div
        style={{
          padding: wide ? "4px 0 20px" : "4px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          maxWidth: wide ? 640 : undefined,
          margin: wide ? "0 auto" : undefined,
          width: "100%",
        }}
      >
        {/* ID card */}
        <CopyTicketCard>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 14 }}>
            <div
              style={{
                color: C.mutedB,
                fontSize: 11.5,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {t.copytrade_id_label}
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 40,
                fontWeight: 700,
                color: C.primary,
                letterSpacing: "0.02em",
              }}
            >
              {COPYTRADE_ID}
            </div>
            <button
              onClick={handleCopy}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "11px 22px",
                borderRadius: 12,
                border: "none",
                background: copied
                  ? C.mutedA
                  : `linear-gradient(120deg, ${C.copyBright} 0%, ${C.copy} 100%)`,
                color: copied ? C.mutedB : "#0b0d13",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                transition: "all .15s ease",
              }}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? t.copytrade_copied : t.copytrade_copy}
            </button>
          </div>
        </CopyTicketCard>

        {/* steps */}
        <div>
          <div style={{ ...labelStyle, marginBottom: 10 }}>{t.copytrade_steps_title}</div>
          <div
            style={{
              background: C.panel1,
              border: `1px solid ${C.hairline}`,
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            {steps.map((step, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 14,
                  padding: "16px",
                  borderTop: i === 0 ? "none" : `1px solid ${C.hairline}`,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: C.copyDeep,
                    border: `1px solid ${C.copy}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 12.5,
                    color: C.copyBright,
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: C.primary,
                      marginBottom: 3,
                    }}
                  >
                    {step.t}
                  </div>
                  <div style={{ fontSize: 12.5, color: C.secondary, lineHeight: 1.45 }}>{step.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* locale-specific settings screenshot — tap/click to open a
            zoomed-in lightbox, since the thumbnail alone is too small
            to read the field labels and values. */}
        <div>
          <div style={{ ...labelStyle, marginBottom: 10 }}>{t.copytrade_screenshot_title}</div>
          <button
            onClick={() => setZoomed(true)}
            style={{
              position: "relative",
              width: "100%",
              background: C.panel1,
              border: `1px solid ${C.hairline}`,
              borderRadius: 16,
              padding: 12,
              display: "flex",
              justifyContent: "center",
              cursor: "zoom-in",
              appearance: "none",
              margin: 0,
            }}
          >
            <img
              src={screenshotSrc}
              alt={t.copytrade_screenshot_alt}
              style={{ width: "100%", maxWidth: 360, borderRadius: 12, display: "block" }}
            />
            <div
              style={{
                position: "absolute",
                right: 22,
                bottom: 22,
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "rgba(11,14,10,0.75)",
                border: `1px solid ${C.hairline}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <ZoomIn size={16} color={C.primary} />
            </div>
          </button>
          <div style={{ fontSize: 11.5, color: C.mutedB, marginTop: 8, textAlign: "center" }}>
            {t.copytrade_screenshot_zoom_hint}
          </div>
        </div>
      </div>

      {zoomed && (
        <div
          onClick={() => setZoomed(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            overflow: "auto",
          }}
        >
          <button
            onClick={() => setZoomed(false)}
            aria-label={t.copytrade_screenshot_close}
            style={{
              position: "fixed",
              top: 18,
              right: 18,
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: C.panel1,
              border: `1px solid ${C.hairline}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <X size={19} color={C.primary} />
          </button>
          <img
            src={screenshotSrc}
            alt={t.copytrade_screenshot_alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 720,
              borderRadius: 14,
              display: "block",
              cursor: "default",
            }}
          />
        </div>
      )}
    </div>
  );
}
